import { useState, useEffect, useCallback } from 'react';
import { MessageCircle, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import styled from 'styled-components';

// Import images
import showcase1 from './assets/showcase1.png';
import showcase2 from './assets/showcase2.png';
import showcase3 from './assets/showcase3.png';
import showcase4 from './assets/showcase4.png';

// Types
interface ShowcaseImage {
  id: number;
  src: string;
  alt: string;
}

const showcaseImages: ShowcaseImage[] = [
  {
    id: 1,
    src: showcase1,
    alt: "StyleX Showcase 1"
  },
  {
    id: 2,
    src: showcase2,
    alt: "StyleX Showcase 2"
  },
  {
    id: 3,
    src: showcase3,
    alt: "StyleX Showcase 3"
  },
  {
    id: 4,
    src: showcase4,
    alt: "StyleX Showcase 4"
  }
];

// Styled Components
// Update the styled components
const Container = styled.div`
    min-height: 100vh;
    background: linear-gradient(to bottom, #0d1117, #161b22);
    color: white;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 4rem 2rem; // Increased top padding
`;

const ContentWrapper = styled.div`
    max-width: 800px;
    width: 100%;
    text-align: center;
    margin-bottom: 3rem; // Space between content and carousel
`;

const Badge = styled.span`
    display: inline-block;
    background: #1da1f2;
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 2rem;
    font-size: 0.9rem;
    font-weight: 500;
    margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  background: linear-gradient(45deg, #1da1f2, #1a91da);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Description = styled.p`
  color: #8b949e;
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const DiscordButton = styled.a`
  background: #5865F2;
  color: white;
  padding: 1rem 2rem;
  border-radius: 0.75rem;
  text-decoration: none;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.2s ease;
  margin-bottom: 3rem;

  &:hover {
    background: #4752C4;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const CarouselContainer = styled.div`
    position: relative;
    width: 100%;
    max-width: 1000px; // Increased from 800px
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
    background: #1a1d21;
`;

const CarouselTrack = styled.div<{ $translate: number }>`
  display: flex;
  transition: transform 0.5s ease-out;
  transform: translateX(${props => -props.$translate * 100}%);
`;

const CarouselItem = styled.div`
  flex: 0 0 100%;
  width: 100%;
`;

const CarouselImage = styled.img`
    width: 100%;
    height: 560px; // Increased from 400px
    object-fit: cover;
    display: block;
`;

const CarouselButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  background-color: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: background-color 0.3s ease;
  z-index: 10;

  &:hover {
    background-color: rgba(0, 0, 0, 0.8);
  }

  &.prev {
    left: 20px;
  }

  &.next {
    right: 20px;
  }
`;

const DotContainer = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
`;

const Dot = styled.button<{ $active: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
  border: none;
  cursor: pointer;
  transition: background-color 0.3s ease;
`;

export default function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % showcaseImages.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + showcaseImages.length) % showcaseImages.length);
  }, []);

  useEffect(() => {
    if (!isPaused) {
      const timer = setInterval(nextSlide, 5000);
      return () => clearInterval(timer);
    }
  }, [isPaused, nextSlide]);

    return (
        <Container>
            <ContentWrapper>
                <Badge>CLOSED BETA</Badge>
                <Title>xppearance</Title>
                <Description>
                    Transform your social media presence with custom themes and styles.
                    Currently in private beta testing phase.
                </Description>
                <DiscordButton
                    href="https://discord.gg/ddS26xz5eJ"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <MessageCircle size={20} />
                    Join Discord Community
                    <ArrowRight size={20} />
                </DiscordButton>
            </ContentWrapper>

            <CarouselContainer
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
            >
          <CarouselTrack $translate={currentSlide}>
            {showcaseImages.map((image) => (
                <CarouselItem key={image.id}>
                  <CarouselImage src={image.src} alt={image.alt} />
                </CarouselItem>
            ))}
          </CarouselTrack>

          <CarouselButton
              className="prev"
              onClick={() => {
                prevSlide();
                setIsPaused(true);
              }}
              aria-label="Previous slide"
          >
            <ChevronLeft size={24} />
          </CarouselButton>

          <CarouselButton
              className="next"
              onClick={() => {
                nextSlide();
                setIsPaused(true);
              }}
              aria-label="Next slide"
          >
            <ChevronRight size={24} />
          </CarouselButton>

          <DotContainer>
            {showcaseImages.map((_, index) => (
                <Dot
                    key={index}
                    $active={index === currentSlide}
                    onClick={() => {
                      setCurrentSlide(index);
                      setIsPaused(true);
                    }}
                    aria-label={`Go to slide ${index + 1}`}
                />
            ))}
          </DotContainer>
        </CarouselContainer>
      </Container>
  );
}